import { apiClient } from "./client";
import type { AppNotification, DataHealthCheck, SyncBatch, ValueKind } from "./types";

// ── Notifications & Teams ────────────────────────────────────────────────────────────────────────

export interface MarkNotificationsReadResult {
  markedCount: number;
}

export interface NotificationPreferenceItem {
  kind: string;
  group: string;
  label: string;
  description: string;
  inAppEnabled: boolean;
  emailEnabled: boolean;
  teamsEnabled: boolean;
  canSwitchOffEmail: boolean;
  canSwitchOffTeams: boolean;
}

export interface NotificationPreferences {
  userPrincipalName: string;
  items: NotificationPreferenceItem[];
}

export interface UpdateNotificationPreferenceItem {
  kind: string;
  inAppEnabled: boolean;
  emailEnabled: boolean;
  teamsEnabled: boolean;
}

export interface TeamsDestination {
  key: string;
  channelName: string;
  deliveryMode: string;
  enabled: boolean;
  updatedAtUtc: string;
  updatedBy: string | null;
}

export interface NotificationSendLogItem {
  id: number;
  idempotencyKey: string;
  recipientUpn: string;
  channel: string;
  status: string;
  failureReason: string | null;
  claimedAtUtc: string;
  completedAtUtc: string | null;
}

export interface TeamsSendLogItem {
  id: number;
  recipientUpn: string;
  destinationKey: string | null;
  kind: string;
  subject: string;
  cardJson: string;
  status: string;
  statusDetail: string;
  createdAtUtc: string;
}

export interface AdminNotificationsOverview {
  catalog: NotificationPreferenceItem[];
  teamsDestinations: TeamsDestination[];
  sendLog: NotificationSendLogItem[];
  teamsDeliveries: TeamsSendLogItem[];
}

export interface SendTeamsTestResult {
  id: number;
  recipientUpn: string;
  destinationKey: string | null;
  status: string;
  statusDetail: string;
  cardJson: string;
  createdAtUtc: string;
}

// ── Data Sync ────────────────────────────────────────────────────────────────────────────────────

export interface SyncRequestItem {
  id: number;
  requestedBy: string;
  requestedAtUtc: string;
  scope: string;
  status: string;
  outOfWindow: boolean;
  custodyId: string | null;
  note: string | null;
}

export interface SyncWindowInfo {
  weekdayWindow: string;
  weekendWindow: string;
  isInsideWindow: boolean;
  plantLocalTime: string;
  nextWindowOpenLocal: string | null;
  summary: string;
}

export interface AdminSyncStatus {
  mode: string;
  enabled: boolean;
  gatewayName: string;
  connectionName: string;
  ingestPipelineName: string;
  validatePipelineName: string;
  weekdayWindow: string;
  weekendWindow: string;
  offsetMinutesAfterEngineering: number;
  disabledReason: string | null;
  window?: SyncWindowInfo;
  heldRequests?: SyncRequestItem[];
  batches: SyncBatch[];
  fabric?: GssFabricStatus | null;
  /** D-O98: the per-job copy of GSS's operation instruction text. */
  instructionText?: GssWoTextStatus | null;
  /** O-R108 (D-O147): distinct part numbers currently governed in gss.import.excluded_parts. */
  excludedPartsCount?: number;
  /** O-R108 (D-O147): work orders excluded by gss.import.excluded_parts on the latest succeeded import batch. */
  lastBatchExcludedWorkOrdersCount?: number;
}

/** One open job whose instruction text GSS cannot read, or whose copy keeps failing (D-O98). */
export interface GssWoTextProblem {
  job: string;
  status: string;
  lastAttemptUtc: string | null;
  attempts: number;
  reason: string | null;
}

/** The instruction-text panel of the Data sync page (D-O98). */
export interface GssWoTextStatus {
  canRun: boolean;
  pipelineName: string;
  openJobs: number;
  copied: number;
  pending: number;
  damaged: number;
  noText: number;
  batchSize: number;
  maxJobsPerIntradayRun: number;
  maxJobsPerBaselineRun: number;
  problems: GssWoTextProblem[];
  lastRunUtc: string | null;
  lastRunSummary: string | null;
  valueKind: ValueKind;
}

/** One Fabric GSS run (an app.sync_batches row with a "Fabric GSS" profile). */
export interface GssFabricRun {
  syncBatchId: number;
  stagedBatchId: string | null;
  fabricState: string;
  outcome: string;
  startedAtUtc: string;
  finishedAtUtc: string | null;
  message: string | null;
}

/** The Fabric panel of the Data sync page (port of Summit Engineering's GSS connection panel). */
export interface GssFabricStatus {
  running: boolean;
  configurationProblems: string[];
  workspaceId: string | null;
  pipelineId: string | null;
  pipelineUrl: string | null;
  stagingDatabase: string;
  stagingServer: string | null;
  intervalMinutes: number;
  warmUpMinutes: number;
  closeMarginMinutes: number;
  nextScheduledRunLocal: string | null;
  statusText: string;
  lastRun: GssFabricRun | null;
  lastImport: GssFabricRun | null;
  recentRuns: GssFabricRun[];
}

export interface RefreshSyncPayload {
  scope?: string;
  reason: string;
  confirmOutOfWindow?: boolean;
  attestation?: string;
}

export interface UpdateSyncWindowPayload {
  weekdayWindow: string;
  weekendWindow: string;
  reason: string;
}

// ── Data Health ──────────────────────────────────────────────────────────────────────────────────

export interface DataHealthTableCount {
  tableName: string;
  label: string;
  rowCount: number;
  valueKind: ValueKind;
}

export interface DataHealthIssueKind {
  kind: string;
  label: string;
  count: number;
  valueKind: ValueKind;
}

export interface AdminDataHealth {
  generatedAtUtc: string;
  dataSource: string;
  checks: DataHealthCheck[];
  tableCounts?: DataHealthTableCount[];
  issueKinds?: DataHealthIssueKind[];
}

export interface DataHealthIssueRow {
  key: string;
  title: string;
  workOrderKey: string | null;
  workcenterCode: string | null;
  detail: string;
  metricValue: number | null;
  metricUnit: string | null;
  valueKind: ValueKind;
}

export interface DataHealthIssuesResponse {
  kind: string;
  label: string;
  totalCount: number;
  countValueKind: ValueKind;
  rows: DataHealthIssueRow[];
}

// ── Communications & Report Scheduler ────────────────────────────────────────────────────────────

export interface EmailTemplateItem {
  key: string;
  title: string;
  subject: string;
  heading: string;
  body: string;
  buttonLabel: string;
  linkPath: string;
  senderDisplayName: string;
}

export interface EmailTemplatePreview {
  key: string;
  subject: string;
  senderDisplayName: string;
  html: string;
  plainText: string;
}

export interface SendTestEmailResult {
  templateKey: string;
  intendedRecipient: string;
  deliveredRecipients: string[];
  suppressedRecipients: string[];
  pilotMode: boolean;
  status: string;
  detail: string;
}

export interface ReportScheduleItem {
  kind: string;
  label: string;
  description: string;
  enabled: boolean;
  dayRule: string;
  sendTimeLocal: string;
  recipients: string[];
  updatedAtUtc: string;
  updatedBy: string | null;
}

export interface ReportSchedulerOverview {
  pilotMode: boolean;
  pilotRecipients: string[];
  leadershipRecipients: string[];
  schedules: ReportScheduleItem[];
}

export interface UpdateReportSchedulePayload {
  enabled: boolean;
  dayRule?: string;
  sendTimeLocal: string;
  recipients?: string[];
  reason: string;
}

export interface UpdateReportRecipientsPayload {
  pilotMode: boolean;
  pilotRecipients: string[];
  leadershipRecipients: string[];
  reason: string;
}

export interface ReportRunHistoryItem {
  id: number;
  kind: string;
  periodKey: string;
  attemptedAtUtc: string;
  outcome: string;
  detail: string;
  triggeredBy: string;
}

// ── Wall Displays Admin ──────────────────────────────────────────────────────────────────────────

export interface WallDisplayItem {
  key: string;
  title: string;
  boardKind: string;
  shopCode: string | null;
  panels: string[];
  refreshSeconds: number;
  enabled: boolean;
  updatedAtUtc: string;
  updatedBy: string | null;
  contentNote: string;
}

export interface UpdateWallDisplayPayload {
  key: string;
  title: string;
  boardKind: string;
  shopCode: string | null;
  panels: string[];
  refreshSeconds: number;
  enabled: boolean;
  reason: string;
}

// ── Feedback ─────────────────────────────────────────────────────────────────────────────────────

export interface FeedbackItem {
  id: number;
  source: string;
  submittedByUpn: string;
  page: string | null;
  helpful: boolean | null;
  reason: string | null;
  text: string;
  aiInteractionId: string | null;
  status: string;
  dispositionNote: string | null;
  triagedByUpn: string | null;
  createdAtUtc: string;
  triagedAtUtc: string | null;
}

export interface SubmitFeedbackPayload {
  page?: string;
  text: string;
  source?: string;
  helpful?: boolean;
  reason?: string;
}

export interface UpdateFeedbackDispositionPayload {
  status: string;
  note: string;
}

// ── Adoption ─────────────────────────────────────────────────────────────────────────────────────

export interface ScreenAdoptionItem {
  routeTemplate: string;
  views: number;
  valueKind: ValueKind;
}

export interface RoleAdoptionItem {
  role: string;
  views: number;
  valueKind: ValueKind;
}

export interface AdoptionOverview {
  days: number;
  retentionDays: number;
  retentionValueKind: ValueKind;
  totalViews: number;
  totalViewsValueKind: ValueKind;
  privacyNotice: string;
  byScreen: ScreenAdoptionItem[];
  byRole: RoleAdoptionItem[];
}

// ── ROI ──────────────────────────────────────────────────────────────────────────────────────────

export interface RoiKindRow {
  kind: string;
  label: string;
  group: string;
  eventCount: number;
  eventCountValueKind: ValueKind;
  baselineMinutes: number;
  baselineValueKind: ValueKind;
  minutesSaved: number;
  minutesSavedValueKind: ValueKind;
  hoursSaved: number;
  hoursSavedValueKind: ValueKind;
  rateRole: string;
  hourlyRateUsd: number | null;
  labourSavedUsd: number | null;
  labourSavedValueKind: ValueKind;
  /** The recommended baseline (catalogue default). */
  recommendedMinutes?: number | null;
  /** Where the baseline used to sum the events comes from. */
  baselineSource?: "recommended" | "edited" | "earlier-default" | null;
  /** Who edited the baseline by hand (when baselineSource is "edited"). */
  baselineEditedBy?: string | null;
  countedFrom?: string | null;
  why?: string | null;
}

export interface RoiInputItem {
  category: string;
  key: string;
  label: string;
  numericValue: number;
  secondaryValue: number | null;
  unit: string;
  rationale: string;
  valueKind: ValueKind;
  updatedAtUtc: string | null;
  updatedBy: string | null;
}

export interface RoiOverview {
  totalMinutesSaved: number;
  totalMinutesSavedValueKind: ValueKind;
  totalHoursSaved: number;
  totalHoursSavedValueKind: ValueKind;
  totalLabourSavedUsd: number;
  totalLabourSavedValueKind: ValueKind;
  aiCostAvailable: boolean;
  aiCostUsd: number | null;
  aiCostValueKind: ValueKind | null;
  aiCostNote: string;
  kinds: RoiKindRow[];
  inputs: RoiInputItem[];
}

export interface UpdateRoiInputItemPayload {
  category: string;
  key: string;
  label?: string;
  numericValue: number;
  secondaryValue?: number | null;
  unit?: string;
  rationale?: string;
}

export interface UpdateRoiInputsPayload {
  items: UpdateRoiInputItemPayload[];
  reason: string;
}

export const platformApi = {
  // Notifications
  getNotifications: async (): Promise<AppNotification[]> =>
    (await apiClient.get<AppNotification[]>("/notifications")).data,

  markNotificationRead: async (id: number): Promise<AppNotification> =>
    (await apiClient.post<AppNotification>(`/notifications/${id}/read`)).data,

  markAllNotificationsRead: async (): Promise<MarkNotificationsReadResult> =>
    (await apiClient.post<MarkNotificationsReadResult>("/notifications/read-all")).data,

  getNotificationPreferences: async (): Promise<NotificationPreferences> =>
    (await apiClient.get<NotificationPreferences>("/notifications/preferences")).data,

  updateNotificationPreferences: async (items: UpdateNotificationPreferenceItem[]): Promise<NotificationPreferences> =>
    (await apiClient.put<NotificationPreferences>("/notifications/preferences", { items })).data,

  getAdminNotifications: async (): Promise<AdminNotificationsOverview> =>
    (await apiClient.get<AdminNotificationsOverview>("/admin/notifications")).data,

  updateTeamsSettings: async (destinations: { key: string; channelName: string; deliveryMode: string; enabled: boolean }[], reason: string): Promise<TeamsDestination[]> =>
    (await apiClient.put<TeamsDestination[]>("/admin/notifications/teams/settings", { destinations, reason })).data,

  sendTeamsTest: async (destinationKey?: string, message?: string): Promise<SendTeamsTestResult> =>
    (await apiClient.post<SendTeamsTestResult>("/admin/notifications/teams/test", { destinationKey, message })).data,

  // Data Sync
  getSyncStatus: async (): Promise<AdminSyncStatus> =>
    (await apiClient.get<AdminSyncStatus>("/admin/data-sync")).data,

  getSyncWindow: async (): Promise<SyncWindowInfo> =>
    (await apiClient.get<SyncWindowInfo>("/admin/data-sync/window")).data,

  updateSyncWindow: async (body: UpdateSyncWindowPayload): Promise<SyncWindowInfo> =>
    (await apiClient.put<SyncWindowInfo>("/admin/data-sync/window", body)).data,

  refreshSyncNow: async (body: RefreshSyncPayload): Promise<SyncRequestItem> =>
    (await apiClient.post<SyncRequestItem>("/admin/data-sync/refresh", body)).data,

  // Data Health
  getDataHealth: async (): Promise<AdminDataHealth> =>
    (await apiClient.get<AdminDataHealth>("/admin/data-health")).data,

  getDataHealthIssues: async (kind: string, take = 50): Promise<DataHealthIssuesResponse> =>
    (await apiClient.get<DataHealthIssuesResponse>("/admin/data-health/issues", { params: { kind, take } })).data,

  // Communications & Reports
  getEmailTemplates: async (): Promise<EmailTemplateItem[]> =>
    (await apiClient.get<EmailTemplateItem[]>("/admin/communications/templates")).data,

  previewEmailTemplate: async (key: string): Promise<EmailTemplatePreview> =>
    (await apiClient.get<EmailTemplatePreview>(`/admin/communications/templates/${encodeURIComponent(key)}/preview`)).data,

  sendTestEmailTemplate: async (key: string): Promise<SendTestEmailResult> =>
    (await apiClient.post<SendTestEmailResult>(`/admin/communications/templates/${encodeURIComponent(key)}/send-test`)).data,

  getReportsOverview: async (): Promise<ReportSchedulerOverview> =>
    (await apiClient.get<ReportSchedulerOverview>("/admin/reports")).data,

  updateReportSchedule: async (kind: string, body: UpdateReportSchedulePayload): Promise<ReportScheduleItem> =>
    (await apiClient.put<ReportScheduleItem>(`/admin/reports/${encodeURIComponent(kind)}/schedule`, body)).data,

  updateReportRecipients: async (body: UpdateReportRecipientsPayload): Promise<ReportSchedulerOverview> =>
    (await apiClient.put<ReportSchedulerOverview>("/admin/reports/recipients", body)).data,

  getReportHistory: async (): Promise<ReportRunHistoryItem[]> =>
    (await apiClient.get<ReportRunHistoryItem[]>("/admin/reports/history")).data,

  runReportKindNow: async (kind: string): Promise<ReportRunHistoryItem> =>
    (await apiClient.post<ReportRunHistoryItem>(`/admin/reports/${encodeURIComponent(kind)}/run-now`)).data,

  // Wall Displays
  getWallDisplays: async (): Promise<WallDisplayItem[]> =>
    (await apiClient.get<WallDisplayItem[]>("/admin/wall-displays")).data,

  updateWallDisplay: async (body: UpdateWallDisplayPayload): Promise<WallDisplayItem> =>
    (await apiClient.put<WallDisplayItem>("/admin/wall-displays", body)).data,

  // Feedback
  submitFeedback: async (body: SubmitFeedbackPayload): Promise<FeedbackItem> =>
    (await apiClient.post<FeedbackItem>("/feedback", body)).data,

  getAdminFeedback: async (status?: string): Promise<FeedbackItem[]> =>
    (await apiClient.get<FeedbackItem[]>("/admin/feedback", { params: status ? { status } : undefined })).data,

  updateFeedbackDisposition: async (id: number, body: UpdateFeedbackDispositionPayload): Promise<FeedbackItem> =>
    (await apiClient.put<FeedbackItem>(`/admin/feedback/${id}/disposition`, body)).data,

  // Adoption
  recordUsageEvents: async (routeTemplates: string[]): Promise<{ acceptedCount: number }> =>
    (await apiClient.post<{ acceptedCount: number }>("/usage/events", {
      events: routeTemplates.map((routeTemplate) => ({ routeTemplate }))
    })).data,

  getAdoption: async (days: 7 | 30 | 90 = 30): Promise<AdoptionOverview> =>
    (await apiClient.get<AdoptionOverview>("/admin/adoption", { params: { days } })).data,

  // ROI
  getRoi: async (): Promise<RoiOverview> =>
    (await apiClient.get<RoiOverview>("/admin/roi")).data,

  updateRoiInputs: async (body: UpdateRoiInputsPayload): Promise<RoiOverview> =>
    (await apiClient.put<RoiOverview>("/admin/roi/inputs", body)).data
};

export const PLATFORM_QK = {
  notifications: ["notifications"] as const,
  notificationPreferences: ["notifications", "preferences"] as const,
  adminNotifications: ["admin", "notifications"] as const,
  dataSync: ["admin", "data-sync"] as const,
  dataHealth: ["admin", "data-health"] as const,
  dataHealthIssues: (kind: string) => ["admin", "data-health", "issues", kind] as const,
  emailTemplates: ["admin", "communications", "templates"] as const,
  emailPreview: (key: string) => ["admin", "communications", "preview", key] as const,
  reportsOverview: ["admin", "reports"] as const,
  reportHistory: ["admin", "reports", "history"] as const,
  wallDisplays: ["admin", "wall-displays"] as const,
  feedback: (status?: string) => ["admin", "feedback", status ?? "all"] as const,
  adoption: (days: number) => ["admin", "adoption", days] as const,
  roi: ["admin", "roi"] as const
};
